/* eslint jsx-a11y/control-has-associated-label: 0 */

import React from "react";
import { Alert, Box, Input, Select, SelectOption } from "@showitinc/showit-wc";
import { List, Map } from "immutable";
import getSlug from "speakingurl";

import NameSlugInput from "../../../components/NameSlugInput";
import AppConstants from "../../../constants/AppConstants";
import WP, { WP_TEMPLATE_OPTIONS } from "../../../constants/WP";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import UIActions from "../../../actions/UIActions";
import HelpConstants from "../../../constants/HelpConstants";
import CanvasSetUpdate from "../../../vo/CanvasSetUpdate";
import { formatPropUpdate } from "../../../utils/PropertyUtils";

const { WP_TEMPLATE_HEIRARCHY, WP_CUSTOM_PAGE_TEMPLATES } = HelpConstants;
const { UPDATE_ACTION_SET, SELECT_EMPTY_OPTION_TEXT } = AppConstants;

/**
 * Page properties info panel
 * @param {{
 *  name: string,
 *  slug: string,
 *  pageId: string,
 *  page: Map
 * }} props
 * @returns
 */
const PagePropertiesInfo = ({ blockContainer, name, slug, pageId, page }) => {
  const _onWPChange = (propInfo) => {
    const data = {
      id: pageId,
      name: propInfo.name,
      value: propInfo.value,
    };
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_PAGE_WP_TEMPLATE, payload: data });
  };

  const _onWPCustomNameChange = (propInfo) => {
    const { value } = propInfo;
    _onWPChange({ ...propInfo, value: getSlug(value) });
  };

  /**
   * Reformats propInfo update and sends it to updateSiteCanvas
   * @param {{
   *  name: string,
   *  value: string,
   *  origValue: string,
   *  addUndo: boolean,
   *  source: string
   * }} propInfo
   */
  const _onCanvasSetChange = (propInfo) => {
    const updater = new CanvasSetUpdate();
    const { name: propPath, value } = propInfo;
    const newValue = !value ? [] : [value];

    updater.add({ ...propInfo, propPath, value: newValue, action: UPDATE_ACTION_SET });

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_CANVAS_SET, payload: updater });
  };

  const _toggleRenamePopup = () => {
    UIActions.toggleRenamePopup({
      title: "Page",
      itemType: AppConstants.ITEM_TYPE_PAGE,
      itemId: pageId,
      name,
      slug,
      showWarning: true,
    });
  };

  const wpTemplate = page.getIn(["wp", "template"], "");
  const isWP = page.get("wp", false);

  const currentCanvasSetId = blockContainer.getIn(["currentPageItem", "canvasSet"], List()).first() ?? "";

  return (
    <>
      <NameSlugInput
        propName="name"
        name={name}
        slug={slug}
        hideSlug={isWP}
        labelPrefix={isWP ? "" : "Page"}
        onAreaFocus={_toggleRenamePopup}
      />
      <Select
        label="Canvas Set"
        reserveContext
        shift="left"
        value={currentCanvasSetId}
        onSISelect={(e) =>
          _onCanvasSetChange(
            formatPropUpdate(`items.${pageId}.canvasSet`, e.target.value, currentCanvasSetId)
          )
        }
      >
        <SelectOption value="">None</SelectOption>
        {blockContainer.getIn(["canvasSets"], Map()).reduce((result, canvasSet) => {
          const id = canvasSet.get("id");
          const canvasName = canvasSet.get("name");
          return result.push(
            <SelectOption key={id} value={id}>
              {canvasName}
            </SelectOption>
          );
        }, List())}
      </Select>

      {isWP && (
        <Select
          reserveContext
          longLabel
          shift="left"
          label="WordPress Template"
          value={wpTemplate}
          onSISelect={(e) => _onWPChange(formatPropUpdate("template", e.target.value, wpTemplate))}
        >
          <SelectOption value="">{SELECT_EMPTY_OPTION_TEXT}</SelectOption>
          {WP_TEMPLATE_OPTIONS.map(({ key, label }, i) => (
            <SelectOption key={`${key}${i}`} value={key} disabled={key === "disabled"}>
              {label}
            </SelectOption>
          ))}
        </Select>
      )}
      {wpTemplate === WP.TEMPLATE_CUSTOM && (
        <>
          <Input
            label="Template Name"
            value={page.getIn(["wp", "custom"], "")}
            placeholder="Template Name"
            onSIChange={(e) =>
              _onWPCustomNameChange(
                formatPropUpdate("custom", e.target.value, page.getIn(["wp", "custom"], ""))
              )
            }
          />
          <Alert
            variant="info"
            stacked
            weight="bold"
            message="This option allows you to define a custom theme template using WordPress' template hierarchy"
            ctaText="Learn More"
            ctaLink={WP_TEMPLATE_HEIRARCHY}
          />
        </>
      )}
      {wpTemplate === WP.TEMPLATE_GLOBAL && (
        <Alert
          variant="info"
          stacked
          weight="bold"
          message='This option allows you to define a custom page template for global use. Name your template using the template "Name" field above'
          ctaText="Learn More"
          ctaLink={WP_CUSTOM_PAGE_TEMPLATES}
        />
      )}
    </>
  );
};

export default PagePropertiesInfo;
